import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Grid,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Center,
  useColorModeValue
} from '@chakra-ui/react';
import { TimeIcon } from '@chakra-ui/icons';

import MiniCalendar from 'components/Calendar/MiniCalendar';
import { getAvailableSlots } from 'services/appointmentBooking';

// ─── Utilities ───────────────────────────────────────────────────────────────
const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${period}`;
};

const formatDateTitle = (date) =>
  date.toLocaleDateString('es-CL', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

// ─── Component ───────────────────────────────────────────────────────────────
function AvailableSlots({ student, teacherId, teacherName, onSlotSelected, loading, setLoading, error, setError }) {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate]     = useState(new Date());
  const [miniCalDate, setMiniCalDate]       = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const textColor   = useColorModeValue('gray.700', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // ─── Cargar slots para el mes visible en el mini calendario ──────────────
  const loadSlotsForMonth = async (monthStart) => {
    setLoading(true);
    setError('');
    try {
      const start = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
      const end   = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);

      const filters = {
        dateFrom:  toDateKey(start),
        dateTo:    toDateKey(end),
        ...(teacherId ? { teacherId } : {})
      };

      const response = await getAvailableSlots(filters);
      if (response.success) {
        setAvailableSlots(response.data.slots || []);
      } else {
        setError(response.message || 'No se pudieron cargar los horarios disponibles');
      }
    } catch (err) {
      console.error('Error loading available slots:', err);
      setError(err.message || 'Error al cargar horarios disponibles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlotsForMonth(miniCalDate);
  }, [miniCalDate, teacherId]);

  // ─── Derived data ─────────────────────────────────────────────────────────
  // Dates that have at least one available slot (for dot indicators)
  const availableDates = useMemo(
    () => [...new Set(availableSlots.map(s => s.date))],
    [availableSlots]
  );

  // Today's date key for disabling past days
  const todayKey = toDateKey(new Date());

  // Slots grouped by teacherId for the selected date
  const slotsForSelectedDate = useMemo(() => {
    const dayKey = toDateKey(selectedDate);
    const grouped = {};
    availableSlots
      .filter(s => s.date === dayKey)
      .forEach(slot => {
        if (!grouped[slot.teacher_id]) {
          // Prefer the Laravel teacher name (passed as prop) over the generic WP placeholder
          grouped[slot.teacher_id] = { teacher_name: teacherName || slot.teacher_name, slots: [] };
        }
        grouped[slot.teacher_id].slots.push(slot);
      });
    // Sort slots by time
    Object.values(grouped).forEach(g => {
      g.slots.sort((a, b) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, [availableSlots, selectedDate, teacherName]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleDayClick = (date) => {
    const key = toDateKey(date);
    if (key < todayKey) return; // Ignore past days
    setSelectedDate(date);
  };

  const handleMonthChange = (firstDayOfMonth) => {
    setMiniCalDate(firstDayOfMonth);
  };

  const handleSlotClick = (slot) => {
    onSlotSelected({
      ...slot,
      formatted_date: formatDateTitle(new Date(slot.date + 'T00:00:00')),
      formatted_time: `${formatTime(slot.start_time)} - ${formatTime(slot.end_time)}`
    });
  };

  const selectedDateKey = toDateKey(selectedDate);
  const isPastSelected  = selectedDateKey < todayKey;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <Text fontSize="lg" fontWeight="700" color={textColor} mb={1}>
          Seleccionar Horario
        </Text>
        <Text fontSize="sm" color="gray.500">
          Elija el día y hora que mejor se ajuste a su disponibilidad
        </Text>
      </Box>


      {/* Error */}
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ── Two-column layout: MiniCalendar + Time slots ── */}
      <Flex direction={{ base: 'column', md: 'row' }} gap={6} align="flex-start">

        {/* LEFT: Mini Calendar */}
        <Box
          w={{ base: '100%', md: '220px' }}
          minW={{ md: '220px' }}
          flexShrink={0}
          p={3}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
        >
          <MiniCalendar
            date={miniCalDate}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
            onMonthChange={handleMonthChange}
            availableDates={availableDates}
          />
        </Box>

        {/* RIGHT: Time slots for selected day */}
        <Box flex="1" minW={0}>
          {loading ? (
            <Center py={8}>
              <VStack spacing={2}>
                <Spinner size="md" color="blue.500" />
                <Text fontSize="sm" color="gray.500">Cargando horarios...</Text>
              </VStack>
            </Center>
          ) : isPastSelected ? (
            <Center py={8}>
              <Text fontSize="sm" color="gray.500">
                Seleccione un día a partir de hoy
              </Text>
            </Center>
          ) : (
            <VStack spacing={4} align="stretch">
              {/* Day title */}
              <Text
                fontSize="sm"
                fontWeight="600"
                color="blue.600"
                textTransform="capitalize"
              >
                {formatDateTitle(new Date(selectedDateKey + 'T00:00:00'))}
              </Text>

              {Object.keys(slotsForSelectedDate).length === 0 ? (
                <Center py={6}>
                  <VStack spacing={1}>
                    <TimeIcon color="gray.400" />
                    <Text fontSize="sm" color="gray.500">
                      No hay horarios disponibles para este día
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <VStack spacing={4} align="stretch">
                  {Object.values(slotsForSelectedDate).map(teacherData => (
                    <Box key={teacherData.teacher_name}>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>
                        {teacherData.teacher_name}
                      </Text>
                      <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                        {teacherData.slots.map(slot => (
                          <Button
                            key={`${slot.date}-${slot.start_time}-${slot.teacher_id}`}
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            onClick={() => handleSlotClick(slot)}
                            fontSize="sm"
                            fontWeight="500"
                          >
                            {formatTime(slot.start_time)}
                          </Button>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </Box>
      </Flex>

      {/* Footer hint */}
      <HStack spacing={1} color="gray.400" fontSize="xs">
        <TimeIcon w="10px" h="10px" />
        <Text>Duración estimada: 30 minutos</Text>
      </HStack>
    </VStack>
  );
}

export default AvailableSlots;
